import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Trip, TripStatus } from '../types/trip';
import type { UserRole } from '../types/user';

// FE-11 — presentación y permisos de UI de los viajes, en un solo sitio.
// Las cuatro superficies que muestran viajes (histórico del operador, página
// de viajes, expediente y reportes) comparten estas reglas; duplicarlas es
// como acabaron divergiendo los estados del mock y los del contrato.

export const TRIP_STATUS_LABELS: Record<TripStatus, string> = {
  planned: 'Programado',
  in_progress: 'En curso',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

export const TRIP_STATUS_COLORS: Record<TripStatus, string> = {
  planned: '#8B5CF6',
  in_progress: '#3B82F6',
  completed: '#10B981',
  cancelled: '#6B7280',
};

/** `2026-09-10T15:05:00Z` → `10 sep 2026, 10:05` (hora local del navegador). */
export function formatDateTime(iso: string): string {
  return format(parseISO(iso), "d MMM yyyy, HH:mm", { locale: es });
}

export function formatTime(iso: string): string {
  return format(parseISO(iso), 'HH:mm', { locale: es });
}

export function formatDate(iso: string): string {
  return format(parseISO(iso), "d MMM yyyy", { locale: es });
}

/** `135` → `2 h 15 min`. */
export function formatDuration(minutes: number | undefined): string {
  if (minutes === undefined) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/**
 * La diferencia entre lo declarado y lo programado, en minutos. Positivo =
 * tarde. Es LA razón por la que el contrato conserva las dos marcas: sin
 * `scheduledFrom` no se puede decir "salió 40 min tarde", solo "salió".
 */
export function desviacionInicioMin(trip: Trip): number | undefined {
  if (!trip.actualFrom) return undefined;
  return Math.round((Date.parse(trip.actualFrom) - Date.parse(trip.scheduledFrom)) / 60000);
}

export function desviacionFinMin(trip: Trip): number | undefined {
  if (!trip.actualTo) return undefined;
  return Math.round((Date.parse(trip.actualTo) - Date.parse(trip.scheduledTo)) / 60000);
}

/** `-12` → `12 min antes`; `40` → `40 min tarde`; `0` → `a tiempo`. */
export function formatDesviacion(min: number | undefined): string | undefined {
  if (min === undefined) return undefined;
  if (min === 0) return 'a tiempo';
  const abs = Math.abs(min);
  const texto = abs >= 60 ? formatDuration(abs) : `${abs} min`;
  return min > 0 ? `${texto} tarde` : `${texto} antes`;
}

// ---------------------------------------------------------------------------
// Permisos de UI — PRODUCT.md §4.8, fila "Viajes".
//
// ⚠️ Esto decide qué BOTÓN se ve, nunca si la operación es legítima: el
// backend responde 403 y es él quien manda (contrato §7). Ocultar un botón no
// sustituye la decisión del servidor, y por eso la UI trata el 403 como un
// mensaje, no como un imposible.
// ---------------------------------------------------------------------------

export function puedeProgramar(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'supervisor';
}

export function puedeCancelar(role: UserRole | undefined, trip: Trip): boolean {
  if (role !== 'admin' && role !== 'supervisor') return false;
  return trip.status === 'planned' || trip.status === 'in_progress';
}

/**
 * Corregir funciona incluso sobre un viaje COMPLETED — es el caso de uso
 * central, no la excepción. Sobre uno cancelado no: su ventana ya está
 * colapsada y el backend no acepta reabrirlo por esta vía.
 */
export function puedeCorregir(role: UserRole | undefined, trip: Trip): boolean {
  if (role !== 'admin' && role !== 'supervisor') return false;
  return trip.status !== 'cancelled';
}

/**
 * Iniciar y finalizar los hace quien conduce. El Operador solo sobre EL SUYO,
 * y ese "suyo" se comprueba contra el `driverId` del viaje, no contra el rol:
 * la ruta de "Mi Desempeño" la abren también supervisor y admin.
 */
export function puedeIniciar(role: UserRole | undefined, trip: Trip, miDriverId?: string): boolean {
  if (trip.status !== 'planned') return false;
  if (role === 'admin' || role === 'supervisor') return true;
  return role === 'operator' && !!miDriverId && trip.driverId === miDriverId;
}

export function puedeFinalizar(role: UserRole | undefined, trip: Trip, miDriverId?: string): boolean {
  if (trip.status !== 'in_progress') return false;
  if (role === 'admin' || role === 'supervisor') return true;
  return role === 'operator' && !!miDriverId && trip.driverId === miDriverId;
}

/**
 * Traduce un `ApiError` de viajes a algo que una persona pueda leer. El `409`
 * es el caso interesante: no es "algo salió mal", es "ese vehículo ya tiene
 * otro viaje en esa franja", que es una regla del negocio (ADR-007) y la
 * razón de que la pregunta "¿a qué viaje pertenece esta posición?" tenga
 * respuesta única.
 */
export function mensajeErrorViaje(err: unknown, accion: 'programar' | 'corregir' | 'iniciar' | 'finalizar' | 'cancelar'): string {
  const status = (err as { status?: number })?.status;
  const detail = (err as { detail?: string })?.detail;

  if (status === 409) {
    if (accion === 'programar' || accion === 'corregir') {
      return detail ?? 'Ese vehículo ya tiene otro viaje en esa franja horaria. Ajusta el horario o corrige el viaje vecino.';
    }
    if (accion === 'iniciar') return detail ?? 'El viaje ya no está programado, o el inicio declarado pisa el viaje anterior.';
    if (accion === 'finalizar') return detail ?? 'El viaje no está en curso, o el cierre declarado es anterior a su inicio.';
    return detail ?? 'Un viaje completado o ya cancelado no se puede cancelar.';
  }
  if (status === 403) return 'No tienes permiso para esta operación sobre este viaje.';
  if (status === 404) return 'El viaje ya no existe o está fuera de tu alcance.';
  if (status === 400) return detail ?? 'Revisa los datos: el servidor los rechazó.';
  return detail ?? 'No se pudo completar la operación. Inténtalo de nuevo.';
}

// ---------------------------------------------------------------------------
// `<input type="datetime-local">` ↔ ISO-8601 UTC.
//
// El input trabaja SIEMPRE en hora local y sin zona (`2026-09-10T10:05`); el
// contrato solo acepta instantes con zona. Convertir en los dos sentidos es
// obligatorio: mandar el valor crudo del input haría que un viaje programado
// para las 10:05 de Bogotá se guardara como las 10:05 UTC, cinco horas antes
// —y el backend no puede detectarlo, porque es un instante perfectamente
// válido—. La ventana quedaría movida y con ella todo el expediente derivado.
// ---------------------------------------------------------------------------

export function toLocalInputValue(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInputValue(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

// ---------------------------------------------------------------------------
// Rango de consulta del histórico.
//
// ⚠️ El cambio de fondo respecto del mock: el filtro ya NO se aplica en el
// cliente sobre una fecha suelta, sino que viaja al servidor como `from`/`to`,
// y allí filtra por **solape** con la ventana del viaje. Un viaje que empezó
// ayer 23:00 y terminó hoy 02:00 SÍ aparece en "Hoy" — que es lo correcto para
// un histórico de operación, y lo contrario de lo que hacía `MOCK_TRIPS`.
// ---------------------------------------------------------------------------

export type TripDateFilter = 'today' | '7days' | 'month' | 'all';

export const TRIP_DATE_FILTERS: { id: TripDateFilter; label: string }[] = [
  { id: 'today', label: 'Hoy' },
  { id: '7days', label: 'Últimos 7 días' },
  { id: 'month', label: 'Este mes' },
  { id: 'all', label: 'Todos' },
];

export function rangoDeFiltro(filtro: TripDateFilter): { from?: string; to?: string } {
  if (filtro === 'all') return {};
  const ahora = new Date();
  const inicio = new Date(ahora);
  const fin = new Date(ahora);
  fin.setHours(23, 59, 59, 999);

  if (filtro === 'today') {
    inicio.setHours(0, 0, 0, 0);
  } else if (filtro === '7days') {
    inicio.setDate(inicio.getDate() - 6);
    inicio.setHours(0, 0, 0, 0);
  } else {
    // "Este mes" cubre el mes ENTERO, no hasta hoy. Un viaje se programa con
    // antelación: cortar el rango en el día de hoy escondía justo los viajes
    // que todavía no han ocurrido, que son los que hay que vigilar. Medido el
    // 2026-09-11: un viaje programado para el 12 no salía en "Este mes".
    inicio.setDate(1);
    inicio.setHours(0, 0, 0, 0);
    fin.setMonth(fin.getMonth() + 1, 0);
    fin.setHours(23, 59, 59, 999);
  }

  return { from: inicio.toISOString(), to: fin.toISOString() };
}
